/**
 * User API Service
 */

import apiClient from './apiClient';
import { encrypt } from '../utils/encryptionUtils';
import {
  UserDTO,
  CreateUserRequest,
  PageResponse,
} from '../types/user.types';

const BASE_PATH = '/v1/users';

class UserService {
  async createUser(request: CreateUserRequest): Promise<UserDTO> {
    const response = await apiClient.post<UserDTO>(BASE_PATH, request);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("facilitiesUpdated"));
    }
    return response.data;
  }

  async getUserById(userId: string): Promise<UserDTO> {
    const response = await apiClient.get<UserDTO>(`/v1/users/keycloak/${userId}`);
    return response.data;
  }

  async getUserByUsername(username: string): Promise<UserDTO> {
    const response = await apiClient.get<UserDTO>(`${BASE_PATH}/username/${username}`);
    return response.data;
  }

  async getAllUsers(page: number = 0, size: number = 20, search?: string): Promise<PageResponse<UserDTO>> {
    let url = BASE_PATH;
    if (search) {
      url = `${BASE_PATH}/search`;
    }
    const response = await apiClient.get<PageResponse<UserDTO>>(url, {
      params: { page, size, sort: 'createdAt', search },
    });
    return response.data;
  }

  async getUsersByRole(role: string): Promise<UserDTO[]> {
    const response = await apiClient.get<UserDTO[]>(`${BASE_PATH}/role/${role}`);
    return response.data;
  }

  async updateUser(id: string, data: Partial<UserDTO>): Promise<UserDTO> {
    const response = await apiClient.put<UserDTO>(`${BASE_PATH}/${id}`, data);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("facilitiesUpdated"));
    }
    return response.data;
  }

  async toggleUserStatus(id: string, activate: boolean): Promise<UserDTO> {
    const response = await apiClient.patch<UserDTO>(`${BASE_PATH}/${id}/toggle`, null, {
      params: { activate },
    });
    return response.data;
  }

  async unlockUser(userId: string): Promise<any> {
    const response = await apiClient.put(`${BASE_PATH}/${userId}/unlock`);
    return response.data;
  }

  async resetPassword(userId: string, data: { newPassword: string; confirmPassword: string }): Promise<void> {
    const encryptedData = {
      newPassword: encrypt(data.newPassword),
      confirmPassword: encrypt(data.confirmPassword)
    };
    await apiClient.put(`${BASE_PATH}/${userId}/reset-password`, encryptedData);
  }

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`);
  }

  /** Hard-delete a user — permanently removes the record even when a soft-delete would conflict. */
  async hardDeleteUser(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}/hard`);
  }

  async getActiveUsers(): Promise<UserDTO[]> {
    const response = await apiClient.get<UserDTO[]>(`${BASE_PATH}/active`);
    return response.data;
  }

  async getCaregiversByFacility(facilityId: string): Promise<UserDTO[]> {
    const response = await apiClient.get<UserDTO[]>(`${BASE_PATH}/caregivers/${facilityId}`);
    return response.data;
  }

  async getUsersCount(): Promise<number> {
    const response = await apiClient.get<number>(`${BASE_PATH}/count`);
    return response.data;
  }

  async uploadSignature(userId: string, signatureDataUrl: string): Promise<any> {
    let cleanBase64 = signatureDataUrl;
    if (signatureDataUrl.includes(',')) {
      cleanBase64 = signatureDataUrl.split(',')[1];
    }
    const params = new URLSearchParams();
    params.append('base64', cleanBase64);

    const response = await apiClient.post(`${BASE_PATH}/${userId}/signature/upload`, params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  }

  async getSignature(userId: string): Promise<string | null> {
    try {
      const response = await apiClient.get(`${BASE_PATH}/${userId}/signature`, {
        responseType: 'blob',
      });
      if (response.data && response.data instanceof Blob && response.data.size > 0) {
        return new Promise<string | null>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (typeof reader.result === 'string') {
              resolve(reader.result);
            } else {
              resolve(null);
            }
          };
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(response.data);
        });
      }
      let sigData = response.data;
      if (sigData) {
        if (typeof sigData === 'object' && !(sigData instanceof Blob)) {
          sigData = sigData.base64 || sigData.signature || sigData.url || sigData.data || '';
        }
        if (typeof sigData === 'string' && sigData.trim()) {
          const str = sigData.trim();
          if (str.startsWith('data:image') || str.startsWith('http://') || str.startsWith('https://')) {
            return str;
          }
          return `data:image/png;base64,${str}`;
        }
      }
      return null;
    } catch (error) {
      console.warn('Get signature API error:', error);
      return null;
    }
  }

  async deleteSignature(userId: string): Promise<void> {
    try {
      await apiClient.delete(`${BASE_PATH}/${userId}/signature/remove`);
    } catch (error) {
      console.warn('Delete signature API error:', error);
    }
  }
}

export default new UserService();
